import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { ApiServiceBase } from '../../../Services/api.service.base';
import { OrganizationsService } from '../../../Services/organizations.service';
import { GuidanceFeedService } from '../../../Services/guidancefeed.service';

declare var google: any;

interface MapDevice {
  Id: number;
  GuidId?: string;
  Name: string;
  DeviceType: string;
  GeoLocationPoints?: number[]; // For point-based devices (gateways, signs, etc.) - [lng, lat]
  GeoPolygonPoints?: number[][]; // For polygon-based devices (parking spaces) - [[lng, lat], ...]
  ChipId?: number;
  Status: string;
  LastCheckinOn?: string;
  GatewayChipId?: number;
  ParkingLevelId?: string;
  ParkingLotId?: string;
  LocationString: string;
  AdditionalData?: any;
  OccupiedSpaces?: number; // For parking spaces: 1 if occupied, 0 if available
  OccupancyStatus?: string; // For parking spaces: Occupied, Available, Reserved, etc.
}

interface ParkingLotPolygon {
  Id: string; // PascalCase to match API response
  Name: string;
  Type: string;
  GeoPolygonPoints: number[][]; // Consistent naming with backend
  GeoCenterPoint: number[]; // Consistent naming with backend
  DisplayColor: string;
  Levels: ParkingLevelInfo[];
}

interface ParkingLevelInfo {
  Id: string; // PascalCase to match API response
  Name: string;
  LevelNumber: number;
}

interface MapDataResponse {
  Devices: MapDevice[];
  ParkingLots: ParkingLotPolygon[];
}

interface MapDataRequest {
  LevelNumber?: number | null;
  ParkingLotId?: string | null;
}

interface DeviceDetailResponse {
  Device: MapDevice;
  RecentMessages: HardwareMessage[];
  DeviceSpecificData: any;
}

interface RecentMessagesResponse {
  RecentMessages: HardwareMessage[];
}

interface HardwareMessage {
  id: number;
  timestamp: string; // Already string - represents local time from server
  messageType: string;
  payloadSummary: string;
}

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: false
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  private map: any;
  private markers: any[] = [];
  private lotPolygons: any[] = [];   // Parking lot outlines
  private spacePolygons: any[] = []; // Parking space polygons
  private polylines: any[] = [];
  private infoWindow: any;
  private labelMarkers: any[] = []; // Track parking lot label markers
  private lotClusterMarkers: any[] = []; // Clustered lot labels when overlapping
  private lotLabelAnchors: { lat: number; lng: number; lotId: string }[] = []; // Used to avoid overlapping labels
  private spaceLabels: any[] = []; // Track parking space label markers
  private spaceStatusMarkers: any[] = []; // Track parking space status markers
  private spacePolygonMap: Map<string, any> = new Map(); // Track parking space polygons by GUID ID
  private spaceStatusMarkerMap: Map<string, any> = new Map(); // Track parking space status markers by GUID ID
  private searchMarkers: any[] = []; // Track search result markers
  private connectionLines: any[] = []; // Track device connection lines
  private selectedDeviceMarker: any = null; // Track the selected device indicator marker
  private polygonListeners: any[] = []; // Map event listeners for polygon drawing
  private drawingPolygon: any = null;
  private polygonVertices: { lat: number; lng: number }[] = [];
  isPolygonDrawing = false;
  hasCompletedPolygonSelection = false;
  private isPolygonDragging = false;
  private dragStartPoint: { lat: number; lng: number } | null = null;
  private previewPolygon: any = null;
  private boundEscapeKeyHandler = (event: KeyboardEvent) => this.handleEscapeKey(event);
  private panAnimationFrame: number | null = null;
  
  devices: MapDevice[] = [];
  parkingLots: ParkingLotPolygon[] = [];
  showDeviceInfo = false;
  selectedDevice: MapDevice | null = null;
  selectedDevices: MapDevice[] = [];
  
  // Search functionality
  searchQuery = '';
  searchResults: MapDevice[] = [];
  showSearchResults = false;
  private searchDebounceTimeout: any;
  deviceDetail: DeviceDetailResponse | null = null;
  loading = false;
  isLoadingDeviceDetail = false;
  isLoadingMessages = false;
  mapViewMode: 'roadmap' | 'satellite' = 'roadmap';
  showParkingLotPolygons = true;
  showLotNames = false;
  showSpaceNames = false;
  showStatusDots = false;
  deviceInfoCollapsed = false;
  configCollapsed = false;
  spaceDetailsCollapsed = false;
  gatewayConfigCollapsed = false;
  signConfigCollapsed = false;
  carCounterConfigCollapsed = false;
  genericConfigCollapsed = false;

  // Global level selection state management (for entire organization)
  // Grouped by LevelNumber so we only show one entry per numeric level
  allLevels: number[] = [];
  selectedGlobalLevelNumber: number | null = null; // null = ALL levels
  selectedParkingLotId: string | null = null; // null = ALL lots

  // Smart data loading state
  private pendingRequest: Promise<any> | null = null;
  private pendingSelectionRestore: { id: number; deviceType: string } | null = null;

  // Device type colors and shapes
  deviceStyles = {
    Gateway: { color: '#FF6B6B', shape: 'triangle' },
    Sign: { color: '#4ECDC4', shape: 'diamond' },
    GuidanceLight: { color: '#45B7D1', shape: 'hexagon' },
    CarCounter: { color: '#96CEB4', shape: 'circle' },
    ParkingSpace: { color: '#FFEAA7', shape: 'polygon' },
    Gate: { color: '#DDA0DD', shape: 'diamond' },
    Lane: { color: '#98D8C8', shape: 'diamond' },
    LocalController: { color: '#F7DC6F', shape: 'star' }
  };

  // Visibility toggles per device type (UI checkboxes)
  visibleDeviceTypes: { [deviceType: string]: boolean } = {
    Gateway: true,
    Sign: true,
    GuidanceLight: true,
    CarCounter: true,
    ParkingSpace: true,
    Gate: true,
    Lane: true,
    LocalController: true
  };

  constructor(
    private apiService: ApiServiceBase,
    private toastr: ToastrService,
    private organizationsService: OrganizationsService,
    private ngZone: NgZone,
    private guidanceFeedService: GuidanceFeedService
  ) {
    // Subscribe to individual parking space occupancy events from Guidance Feed Service Bus
    this.guidanceFeedService.GuidanceFeedServiceUpdated.subscribe((event: any) => {
      this.handleIndividualSpaceOccupancyEvent(event);
    });
  }

  toggleDeviceType(deviceType: 'ParkingSpace' | 'GuidanceLight' | 'CarCounter' | 'Sign' | 'Gateway') {
    this.visibleDeviceTypes[deviceType] = !this.visibleDeviceTypes[deviceType];
    this.drawDevices();
  }


  ngOnInit() {
    this.loadParkingLotPolygons();
  }

  async ngAfterViewInit() {
    await this.initializeMap();
  }

  ngOnDestroy() {
    // Clean up map resources
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
    
    // Clean up label markers
    this.labelMarkers.forEach(marker => marker.map = null);
    this.labelMarkers = [];
    this.spaceLabels.forEach(label => label.map = null);
    this.spaceLabels = [];
    this.spaceStatusMarkers.forEach(marker => marker.map = null);
    this.spaceStatusMarkers = [];
    this.spacePolygonMap.clear();
    this.spaceStatusMarkerMap.clear();
    this.clearSearchMarkers();
    this.clearConnectionLines();
    this.clearSelectedDeviceIndicator();

    // Clean up polygon drawing artefacts and listeners
    this.clearPolygonDrawing();
  }

  private async initializeMap() {
    // Get organization center point
    let centerLat = 40.7128; // Default to NYC
    let centerLng = -74.0060;
    
    try {
      const orgLocation = await this.organizationsService.GetOrganizationLocation();
      // console.log('Organization location from service:', orgLocation);
      console.log('Type of orgLocation[0]:', typeof orgLocation[0], 'Value:', orgLocation[0]);
      console.log('Type of orgLocation[1]:', typeof orgLocation[1], 'Value:', orgLocation[1]);
      
      if (orgLocation && orgLocation.length >= 2 && orgLocation[0] !== 0 && orgLocation[1] !== 0) {
        centerLng = Number(orgLocation[0]); // Ensure it's a number
        centerLat = Number(orgLocation[1]); // Ensure it's a number
        console.log('Converted coordinates - Lat:', centerLat, 'Lng:', centerLng);
        console.log('Type of centerLat:', typeof centerLat, 'Type of centerLng:', typeof centerLng);
        console.log('Is centerLat valid number?', !isNaN(centerLat) && isFinite(centerLat));
        console.log('Is centerLng valid number?', !isNaN(centerLng) && isFinite(centerLng));
      } else {
        // console.log('Organization location invalid or empty, using default NYC coordinates');
      }
    } catch (error) {
      console.warn('Could not get organization location, using default:', error);
    }

    // Validate coordinates before creating map
    if (isNaN(centerLat) || isNaN(centerLng) || !isFinite(centerLat) || !isFinite(centerLng)) {
      console.error('Invalid coordinates detected, falling back to NYC');
      centerLat = 40.7128;
      centerLng = -74.0060;
    }

    // Validate coordinate ranges
    if (centerLat < -90 || centerLat > 90) {
      console.error('Invalid latitude range:', centerLat, 'falling back to NYC');
      centerLat = 40.7128;
      centerLng = -74.0060;
    }
    
    if (centerLng < -180 || centerLng > 180) {
      console.error('Invalid longitude range:', centerLng, 'falling back to NYC');
      centerLat = 40.7128;
      centerLng = -74.0060;
    }

    const mapTypeId =
      this.mapViewMode === 'satellite'
        ? google.maps.MapTypeId.SATELLITE
        : google.maps.MapTypeId.ROADMAP;

    const mapOptions = {
      center: { lat: centerLat, lng: centerLng },
      zoom: 18,
      mapTypeId,
      mapTypeControl: false,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true,
      mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement,
      disableTilt: true,
      disableRotate: true,
      disableDefaultUI: true,
    };

    console.log('Final validated coordinates - Lat:', centerLat, 'Lng:', centerLng);
    console.log('Map options object:', mapOptions);
    this.map = new google.maps.Map(this.mapContainer.nativeElement, mapOptions);

    // Force a flat, non-tilted overhead view (no 3D buildings)
    this.enforceFlatOverheadView();
    
    // Explicitly set the center after map creation using multiple methods
    const targetCenter = new google.maps.LatLng(centerLat, centerLng);
    console.log('Target center object:', targetCenter, 'Lat:', targetCenter.lat(), 'Lng:', targetCenter.lng());
    
    this.map.setCenter(targetCenter);
    console.log('Called setCenter with:', targetCenter.lat(), targetCenter.lng());
    
    // Also try panTo as backup
    this.map.panTo(targetCenter);
    console.log('Called panTo with:', targetCenter.lat(), targetCenter.lng());
    
    // Verify the map center after creation
    setTimeout(() => {
      const actualCenter = this.map.getCenter();
      console.log('Actual map center after creation:', actualCenter.lat(), actualCenter.lng());
    }, 1000);
    this.infoWindow = new google.maps.InfoWindow();

    // Add map event listeners
    this.map.addListener('idle', () => {
      // 'idle' fires after all map changes (pan, zoom, etc.) are complete
      this.debounceLoadMapData();
    });

    this.map.addListener('zoom_changed', () => {
      this.ngZone.run(() => {
        this.recalculateLotLabelClustersIfNeeded();
      });
    });

    // Initial load
    this.loadMapData();
    
    // Draw parking lot polygons if they've already been loaded
    if (this.parkingLots.length > 0) {
      this.drawParkingLotPolygons();
    }
  }

  setMapView(mode: 'roadmap' | 'satellite') {
    if (this.mapViewMode === mode) {
      return;
    }

    this.mapViewMode = mode;

    if (!this.map) {
      return;
    }

    const mapTypeId =
      mode === 'satellite'
        ? google.maps.MapTypeId.SATELLITE
        : google.maps.MapTypeId.ROADMAP;

    this.map.setMapTypeId(mapTypeId);

    // Re-apply flat overhead view whenever map type is changed
    this.enforceFlatOverheadView();
  }

  private debounceTimeout: any;
  private debounceLoadMapData() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(() => {
      this.checkAndLoadMapData();
    }, 500);
  }

  private checkAndLoadMapData() {
    this.loadMapData();
  }

  private loadParkingLotPolygons() {
    this.apiService.Get<ParkingLotPolygon[]>('api/InfrastructureMap/parkinglotpolygons')
      .then((data) => {
          this.parkingLots = data;
          this.allLevels = this.buildGlobalLevels(this.parkingLots, this.selectedParkingLotId);
        console.log(`Loaded ${data.length} parking lots`);
          this.drawParkingLotPolygons();
        
        // Now that parking lots are loaded, trigger map data load if map is ready
        if (this.map) {
          this.forceReloadMapData();
        }
      })
      .catch((error) => {
          console.error('Error loading parking lot polygons:', error);
          this.toastr.error('Failed to load parking lot polygons');
      });
  }

  private buildGlobalLevels(parkingLots: ParkingLotPolygon[], lotId: string | null): number[] {
    const levelSet = new Set<number>();

    parkingLots.forEach(lot => {
      if (lotId && lot.Id !== lotId) {
        return;
      }

      (lot.Levels || []).forEach(level => {
        if (level && level.LevelNumber !== undefined && level.LevelNumber !== null) {
          levelSet.add(level.LevelNumber);
        }
      });
    });

    const levels = Array.from(levelSet.values());
    levels.sort((a, b) => a - b);

    return levels;
  }

  private loadMapData() {
    if (!this.map) return;

    // Cancel any pending request
    if (this.pendingRequest) {
      // Note: We can't actually cancel the HTTP request, but we can ignore its result
    }


    const request: MapDataRequest = {
      LevelNumber: this.selectedGlobalLevelNumber ?? undefined,
      ParkingLotId: this.selectedParkingLotId ?? undefined
    };

    console.log('Loading map data for LevelNumber:', request.LevelNumber ?? 'ALL');

    this.loading = true;
    
    // Store the current request so we can track/cancel it
    this.pendingRequest = this.apiService.Post<MapDataResponse>('api/InfrastructureMap/mapdata', request)
      .then((data) => {
        // Only update if this is still the current request
        if (this.pendingRequest === currentRequest) {
          console.log(`Loaded ${data.Devices.length} devices`);

          // Replace existing devices with the new snapshot
          this.devices = data.Devices;

          this.drawDevices();

          // If a manual refresh was requested while a device was selected, attempt to restore that selection.
          if (this.pendingSelectionRestore) {
            const selection = this.pendingSelectionRestore;
            this.pendingSelectionRestore = null;

            const restored = this.devices.find(d => d.Id === selection.id && d.DeviceType === selection.deviceType);
            if (restored) {
              this.selectedDevice = restored;
              this.showDeviceInfo = true;
              this.showSelectedDeviceIndicator(restored);
              this.isLoadingDeviceDetail = true;
              this.isLoadingMessages = true;
              this.loadDeviceDetail(restored);
              this.loadDeviceMessages(restored);
            } else {
              this.closeDeviceInfo();
            }
          }
          
          this.loading = false;
          this.pendingRequest = null;
        }
      })
      .catch((error) => {
        // Only show error if this is still the current request
        if (this.pendingRequest === currentRequest) {
          console.error('Error loading map data:', error);
          this.toastr.error('Failed to load map data');
          this.loading = false;
          this.pendingRequest = null;
        }
      });

    // Store reference to current request for comparison
    const currentRequest = this.pendingRequest;
  }

  private isParkingLotVisibleInBounds(
    lot: ParkingLotPolygon, 
    swLat: number, 
    swLng: number, 
    neLat: number, 
    neLng: number
  ): boolean {
    // Check if the parking lot center point is within bounds
    const centerLat = lot.GeoCenterPoint[1]; // Y coordinate is latitude
    const centerLng = lot.GeoCenterPoint[0]; // X coordinate is longitude
    
    if (centerLat >= swLat && centerLat <= neLat && centerLng >= swLng && centerLng <= neLng) {
      return true;
    }
    
    // Also check if any polygon points are within bounds
    for (const point of lot.GeoPolygonPoints) {
      const pointLat = point[1]; // Y coordinate is latitude
      const pointLng = point[0]; // X coordinate is longitude
      
      if (pointLat >= swLat && pointLat <= neLat && pointLng >= swLng && pointLng <= neLng) {
        return true;
      }
    }
    
    // Could also check if bounds are entirely within the parking lot polygon
    // But for now, the above checks should be sufficient
    
    return false;
  }

  private drawParkingLotPolygons() {
    // Respect toggle for showing/hiding parking lot polygons
    if (!this.showParkingLotPolygons) {
      // If polygons are currently hidden, ensure any existing ones are cleared
      this.lotPolygons.forEach(polygon => polygon.setMap(null));
      this.lotPolygons = [];
      return;
    }

    // Check if map is initialized before proceeding
    if (!this.map) {
      console.log('Map not yet initialized, parking lot polygons will be drawn after map loads');
      return;
    }

    // Clear existing lot polygons and labels
    this.lotPolygons.forEach(polygon => polygon.setMap(null));
    this.lotPolygons = [];
    this.lotLabelAnchors = [];

    this.labelMarkers.forEach(marker => marker.map = null);
    this.labelMarkers = [];
    this.lotClusterMarkers.forEach(marker => marker.map = null);
    this.lotClusterMarkers = [];
    this.spaceLabels.forEach(label => label.map = null);
    this.spaceLabels = [];
    this.spaceStatusMarkers.forEach(marker => marker.map = null);
    this.spaceStatusMarkers = [];

    this.parkingLots.forEach(lot => {
      // If a specific lot is selected, only outline that lot
      if (this.selectedParkingLotId && lot.Id !== this.selectedParkingLotId) {
        return;
      }

      if (lot.GeoPolygonPoints && lot.GeoPolygonPoints.length > 0) {
        const paths = lot.GeoPolygonPoints.map(point => ({
          lat: point[1], // Y coordinate (latitude)
          lng: point[0]  // X coordinate (longitude)
        }));

        const polygon = new google.maps.Polygon({
          paths: paths,
          strokeColor: `#${lot.DisplayColor}`,
          strokeOpacity: 0.35,
          strokeWeight: 2,
          fillColor: `#${lot.DisplayColor}`,
          fillOpacity: 0.35, // 35% opacity so lot shading is clearly visible
          clickable: true,
          zIndex: 1 // Ensure lot polygons sit behind parking spaces
        });

        polygon.setMap(this.map);
        this.lotPolygons.push(polygon);

        // Create label for this parking lot
        this.createParkingLotLabel(lot);

        // Add click listener for lot selection
        polygon.addListener('click', (event: any) => {
          this.ngZone.run(() => {
            // When drawing a selection polygon, treat clicks on lot polygons as map clicks
            if (this.isPolygonDrawing) {
              this.addPolygonVertex(event);
            } else {
              this.selectParkingLot(lot);
            }
          });
        });
      }
    });

    // Ensure label visibility matches current settings and lot selection
    this.updateLotLabelVisibility();
  }

  onToggleParkingLotPolygons() {
    if (!this.map) {
      return;
    }

    if (this.showParkingLotPolygons) {
      // Redraw polygons for current parking lots/selection
      this.drawParkingLotPolygons();
    } else {
      // Hide existing polygons but leave labels as-is
      this.lotPolygons.forEach(polygon => polygon.setMap(null));
      this.lotPolygons = [];
    }
  }

  private createParkingLotLabel(lot: ParkingLotPolygon) {
    // Find the top-most point along the vertical line through the polygon's centre.
    // This gives a label anchor that is visually centred horizontally and touches
    // the top edge of the lot.
    const centerLng = lot.GeoCenterPoint[0];
    let bestLat = -90; // Southernmost starting point
    let bestLng = centerLng;
    let foundIntersection = false;

    const points = lot.GeoPolygonPoints || [];
    const pointCount = points.length;

    if (pointCount > 1) {
      for (let i = 0; i < pointCount; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % pointCount];

        const x1 = p1[0];
        const y1 = p1[1];
        const x2 = p2[0];
        const y2 = p2[1];

        // Check if the segment crosses the vertical line x = centerLng
        if ((x1 <= centerLng && x2 >= centerLng) || (x2 <= centerLng && x1 >= centerLng)) {
          if (x1 === x2) {
            // Vertical segment exactly on the center line - take the higher endpoint
            const candidateLat = Math.max(y1, y2);
            const candidateLng = x1;
            if (candidateLat > bestLat) {
              bestLat = candidateLat;
              bestLng = candidateLng;
              foundIntersection = true;
            }
          } else {
            // Interpolate intersection point with x = centerLng
            const t = (centerLng - x1) / (x2 - x1);
            if (t >= 0 && t <= 1) {
              const candidateLat = y1 + t * (y2 - y1);
              const candidateLng = centerLng;
              if (candidateLat > bestLat) {
                bestLat = candidateLat;
                bestLng = candidateLng;
                foundIntersection = true;
              }
            }
          }
        }
      }
    }

    // Fallback: if no intersection was found (degenerate polygon), just use the
    // highest vertex closest to the centre.
    if (!foundIntersection && pointCount > 0) {
      points.forEach(point => {
        const lat = point[1];
        const lng = point[0];
        if (lat > bestLat) {
          bestLat = lat;
          bestLng = lng;
        } else if (lat === bestLat) {
          const currentOffset = Math.abs(bestLng - centerLng);
          const candidateOffset = Math.abs(lng - centerLng);
          if (candidateOffset < currentOffset) {
            bestLng = lng;
          }
        }
      });
    }

    const labelLat = bestLat;
    const labelLng = bestLng;

    // Record this anchor so we can optionally use it later for de-cluttering
    this.lotLabelAnchors.push({ lat: labelLat, lng: labelLng, lotId: lot.Id });

    // Create the HTML content for the label
    const labelContent = document.createElement('div');
    labelContent.className = 'parking-lot-label';
    labelContent.style.cssText = `
      background: rgba(255, 255, 255, 0.95);
      border: 2px solid #${lot.DisplayColor};
      border-radius: 6px;
      padding: 8px;
      font-family: Arial, sans-serif;
      font-size: 12px;
      font-weight: bold;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      min-width: 120px;
      text-align: center;
      pointer-events: auto;
      cursor: default;
      z-index: 1000;
      position: relative;
    `;

    // Prevent Google Maps from interfering with label interactions
    labelContent.addEventListener('mousedown', (e) => e.stopPropagation());
    labelContent.addEventListener('mouseup', (e) => e.stopPropagation());
    labelContent.addEventListener('click', (e) => e.stopPropagation());
    labelContent.addEventListener('mouseover', (e) => e.stopPropagation());
    labelContent.addEventListener('mousemove', (e) => e.stopPropagation());

    // Add parking lot name
    const nameDiv = document.createElement('div');
    nameDiv.style.cssText = `
      margin-bottom: 4px;
      color: #333;
      font-size: 13px;
    `;
    nameDiv.textContent = lot.Name;
    labelContent.appendChild(nameDiv);

    // Add a small downward-pointing "pointer" at the bottom center of the label
    const pointerDiv = document.createElement('div');
    pointerDiv.style.cssText = `
      position: absolute;
      left: 50%;
      bottom: -6px;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #${lot.DisplayColor};
    `;
    labelContent.appendChild(pointerDiv);

    // Create Advanced Marker with the label content at offset position
    const labelPosition = { lat: labelLat, lng: labelLng };

    const labelMarker = new google.maps.marker.AdvancedMarkerElement({
      // Actual visibility will be handled by updateLotLabelVisibility
      map: null,
      position: labelPosition,
      content: labelContent,
      title: lot.Name
    });

    (labelMarker as any).lotId = lot.Id;
    this.labelMarkers.push(labelMarker);
  }

  private updateLotLabelVisibility() {
    if (!this.map) {
      return;
    }

    // If lot names are toggled off, hide all labels
    if (!this.showLotNames) {
      this.labelMarkers.forEach(marker => (marker.map = null));
      this.lotClusterMarkers.forEach(marker => (marker.map = null));
      this.lotClusterMarkers = [];
      return;
    }

    // If a specific lot is selected, only show that lot's label
    if (this.selectedParkingLotId) {
      this.lotClusterMarkers.forEach(marker => (marker.map = null));
      this.lotClusterMarkers = [];
      this.labelMarkers.forEach(marker => {
        const lotId = (marker as any).lotId as string | undefined;
        marker.map =
          lotId && lotId === this.selectedParkingLotId
            ? this.map
            : null;
      });
      return;
    }

    // When showing all lots, for now always show individual labels to avoid
    // any chance of labels disappearing due to clustering issues.
    this.lotClusterMarkers.forEach(marker => (marker.map = null));
    this.lotClusterMarkers = [];
    this.labelMarkers.forEach(marker => (marker.map = this.map));
  }

  private recalculateLotLabelClustersIfNeeded() {
    // Clustering of lot labels is currently disabled to keep behaviour simple
    // and predictable. This method is left in place for potential future use.
    if (!this.map) {
      return;
    }
  }

  private getDeviceCoordinates(device: MapDevice): { lat: number, lng: number } | null {
    // For parking spaces, calculate centroid of polygon for better visual connection points
    if (device.GeoPolygonPoints && device.GeoPolygonPoints.length > 0) {
      return this.calculatePolygonCentroid(device.GeoPolygonPoints);
    }
    
    // For point-based devices, use location points
    if (device.GeoLocationPoints && device.GeoLocationPoints.length >= 2) {
      return {
        lat: device.GeoLocationPoints[1], // Y coordinate (latitude)
        lng: device.GeoLocationPoints[0]  // X coordinate (longitude)
      };
    }
    
    return null;
  }

  // Exposed for templates to show coordinates in selection table
  getDeviceLatitude(device: MapDevice): number | null {
    const coords = this.getDeviceCoordinates(device);
    return coords ? coords.lat : null;
  }

  getDeviceLongitude(device: MapDevice): number | null {
    const coords = this.getDeviceCoordinates(device);
    return coords ? coords.lng : null;
  }

  getDeviceConfigUrl(device: MapDevice): string | null {
    if (!device) {
      return null;
    }

    const id = device.Id;

    switch (device.DeviceType) {
      case 'ParkingSpace':
        return `/hardware/devices/spaces/${id}`;
      case 'GuidanceLight':
        return `/hardware/devices/guidancelights/${id}`;
      case 'Sign':
        return `/hardware/devices/signs/${id}`;
      case 'Gateway':
        return `/hardware/devices/gateways/${id}`;
      case 'CarCounter':
        return `/hardware/devices/carcounters/${id}`;
      default:
        return null;
    }
  }

  exportSelectedDevices() {
    if (!this.selectedDevices || this.selectedDevices.length === 0) {
      return;
    }

    const header = [
      'Name',
      'ChipId',
      'Type',
      'Latitude',
      'Longitude',
      'Status',
      'Occupancy',
      'LastCheckIn',
      'ConfigurationUrl'
    ];

    const rows = this.selectedDevices.map(device => {
      const coords = this.getDeviceCoordinates(device);
      const lat = coords ? coords.lat.toFixed(6) : '';
      const lng = coords ? coords.lng.toFixed(6) : '';
      const url = this.getDeviceConfigUrl(device) ?? '';

      const values = [
        device.Name ?? '',
        device.ChipId?.toString() ?? '',
        device.DeviceType ?? '',
        lat,
        lng,
        device.Status ?? '',
        device.OccupancyStatus ?? '',
        device.LastCheckinOn ?? '',
        url
      ];

      // Simple CSV escaping: wrap in quotes and escape existing quotes
      return values
        .map(v => `"${(v || '').toString().replace(/"/g, '""')}"`)
        .join(',');
    });

    const csvContent = [header.join(','), ...rows].join('\r\n');

    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(
      now.getHours()
    )}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const filename = `selected-devices-${timestamp}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private calculatePolygonCentroid(polygonPoints: number[][]): { lat: number, lng: number } {
    // Calculate the arithmetic mean centroid of polygon vertices
    // This is computationally very lightweight (O(n) where n = number of vertices)
    // and provides much better visual results for connection lines
    let totalLat = 0;
    let totalLng = 0;
    const pointCount = polygonPoints.length;
    
    for (const point of polygonPoints) {
      totalLng += point[0]; // longitude (X coordinate)
      totalLat += point[1]; // latitude (Y coordinate)
    }
    
    return {
      lat: totalLat / pointCount,
      lng: totalLng / pointCount
    };
  }

  drawDevices() {
    // Clear existing markers, polylines, parking space polygons, and space labels
    this.markers.forEach(marker => marker.setMap(null));
    this.polylines.forEach(polyline => polyline.setMap(null));
    this.spacePolygons.forEach(polygon => polygon.setMap(null));
    this.spaceLabels.forEach(label => label.map = null);
    this.spaceStatusMarkers.forEach(marker => marker.map = null);
    this.clearConnectionLines(); // Clear connection lines
    this.clearHighlightedSpaces(); // Clear highlighted spaces
    this.markers = [];
    this.polylines = [];
    this.spacePolygons = [];
    this.spaceLabels = [];
    this.spaceStatusMarkers = [];
    this.spacePolygonMap.clear(); // Clear the parking space polygon mapping
    this.spaceStatusMarkerMap.clear();

    this.devices.forEach(device => {
      if (!this.isDeviceTypeVisible(device.DeviceType)) {
        return; // Skip drawing this device if its type is hidden
      }

      if (device.DeviceType === 'ParkingSpace') {
        // Parking spaces use the full polygon
        if (device.GeoPolygonPoints && device.GeoPolygonPoints.length > 0) {
        this.drawParkingSpacePolygon(device);
        }
      } else {
        // Other devices (gateways, signs, etc.) use location points as markers
        if (device.GeoLocationPoints && device.GeoLocationPoints.length >= 2) {
        this.drawDeviceMarker(device);
        }
      }
    });
    
    // Restore selected device indicator if there's a currently selected device
    // and it's still in the current devices list
    if (this.selectedDevice) {
      const deviceStillExists = this.devices.find(d => 
        d.Id === this.selectedDevice!.Id && d.DeviceType === this.selectedDevice!.DeviceType
      );
      
      if (deviceStillExists) {
        this.showSelectedDeviceIndicator(this.selectedDevice);
      } else {
        // Selected device is no longer visible, clear the selection
        this.closeDeviceInfo();
      }
    }

  }

  private isDeviceTypeVisible(deviceType: string | null | undefined): boolean {
    if (!deviceType) {
      return true;
    }

    const value = this.visibleDeviceTypes[deviceType];
    // Default to visible if we don't have an explicit toggle for this type
    return value === undefined ? true : value;
  }

  // ---- Polygon selection drawing ----

  togglePolygonDrawing() {
    if (!this.map) {
      return;
    }

    if (!this.isPolygonDrawing) {
      // Start polygon drawing
      this.isPolygonDrawing = true;
      this.hasCompletedPolygonSelection = false;
      this.polygonVertices = [];
      this.selectedDevices = [];

      if (this.drawingPolygon) {
        this.drawingPolygon.setMap(null);
        this.drawingPolygon = null;
      }

      // Add map listeners for click and drag drawing
      this.polygonListeners.push(
        this.map.addListener('click', (event: any) => this.addPolygonVertex(event))
      );
      this.polygonListeners.push(
        this.map.addListener('mousedown', (event: any) => this.startPolygonDrag(event))
      );
      this.polygonListeners.push(
        this.map.addListener('mousemove', (event: any) => this.updatePolygonPreview(event))
      );
      this.polygonListeners.push(
        this.map.addListener('mouseup', (event: any) => this.endPolygonDrag(event))
      );

      // Escape key cancels drawing
      document.addEventListener('keydown', this.boundEscapeKeyHandler);
    } else {
      // Finish polygon selection
      this.finishPolygonSelection();
    }
  }

  cancelPolygonDrawing() {
    this.clearPolygonDrawing();
    this.selectedDevices = [];
    this.hasCompletedPolygonSelection = false;
  }

  private clearPolygonDrawing(removePolygon: boolean = true) {
    // Remove map listeners
    this.polygonListeners.forEach(listener => google.maps.event.removeListener(listener));
    this.polygonListeners = [];

    document.removeEventListener('keydown', this.boundEscapeKeyHandler);

    if (this.drawingPolygon && removePolygon) {
      this.drawingPolygon.setMap(null);
      this.drawingPolygon = null;
    }

    if (this.previewPolygon) {
      this.previewPolygon.setMap(null);
      this.previewPolygon = null;
    }

    this.isPolygonDrawing = false;
    this.isPolygonDragging = false;
    this.dragStartPoint = null;
    if (removePolygon) {
      this.polygonVertices = [];
    }
  }

  private handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isPolygonDrawing) {
      this.cancelPolygonDrawing();
    }
  }

  private addPolygonVertex(event: any) {
    if (!this.isPolygonDrawing || this.isPolygonDragging) {
      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    this.polygonVertices.push({ lng, lat });
    this.updatePolygonDisplay();
  }

  private startPolygonDrag(event: any) {
    if (!this.isPolygonDrawing) {
      return;
    }

    this.isPolygonDragging = true;
    this.dragStartPoint = { lat: event.latLng.lat(), lng: event.latLng.lng() };

    if (this.polygonVertices.length === 0) {
      this.polygonVertices.push(this.dragStartPoint);
    }
  }

  private updatePolygonPreview(event: any) {
    if (!this.isPolygonDrawing || !this.isPolygonDragging || !this.dragStartPoint || !this.map) {
      return;
    }

    const currentPoint = { lat: event.latLng.lat(), lng: event.latLng.lng() };

    if (this.previewPolygon) {
      this.previewPolygon.setMap(null);
    }

    const previewPath = [this.dragStartPoint, currentPoint];
    this.previewPolygon = new google.maps.Polyline({
      path: previewPath,
      strokeColor: '#6f42c1',
      strokeOpacity: 0.6,
      strokeWeight: 2,
      map: this.map
    });
  }

  private endPolygonDrag(event: any) {
    if (!this.isPolygonDrawing || !this.isPolygonDragging) {
      return;
    }

    this.isPolygonDragging = false;
    const endPoint = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    this.polygonVertices.push(endPoint);

    if (this.previewPolygon) {
      this.previewPolygon.setMap(null);
      this.previewPolygon = null;
    }

    this.updatePolygonDisplay();
  }

  private updatePolygonDisplay() {
    if (!this.map) {
      return;
    }

    if (this.drawingPolygon) {
      this.drawingPolygon.setMap(null);
      this.drawingPolygon = null;
    }

    if (this.polygonVertices.length >= 3) {
      this.drawingPolygon = new google.maps.Polygon({
        paths: this.polygonVertices,
        strokeColor: '#6f42c1',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#6f42c1',
        fillOpacity: 0.2,
        map: this.map
      });
    } else if (this.polygonVertices.length >= 2) {
      this.drawingPolygon = new google.maps.Polyline({
        path: this.polygonVertices,
        strokeColor: '#6f42c1',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        map: this.map
      });
    }
  }

  private finishPolygonSelection() {
    if (this.polygonVertices.length < 3 || !this.drawingPolygon) {
      alert('Please draw a polygon with at least 3 points');
      return;
    }

    // Stop drawing interaction but keep the polygon for selection
    this.clearPolygonDrawing(false);

    // Find devices inside polygon
    this.findDevicesInPolygon();

    // Mark that a polygon selection has been completed so the UI can show the box
    this.hasCompletedPolygonSelection = true;
  }

  private findDevicesInPolygon() {
    if (!this.drawingPolygon && this.polygonVertices.length < 3) {
      return;
    }

    if (!google.maps.geometry || !google.maps.geometry.poly) {
      console.error('Google Maps geometry library not loaded');
      this.toastr.error('Unable to perform polygon selection - geometry library not loaded');
      return;
    }

    this.selectedDevices = [];

    this.devices.forEach(device => {
      if (!this.isDeviceTypeVisible(device.DeviceType)) {
        return;
      }

      const coords = this.getDeviceCoordinates(device);
      if (!coords) {
        return;
      }

      const point = new google.maps.LatLng(coords.lat, coords.lng);

      if (google.maps.geometry && google.maps.geometry.poly &&
          google.maps.geometry.poly.containsLocation(point, this.drawingPolygon)) {
        this.selectedDevices.push(device);
      }
    });
  }

  private drawParkingSpacePolygon(device: MapDevice) {
    if (!device.GeoPolygonPoints) return;

    const paths = device.GeoPolygonPoints.map(point => ({
      lat: point[1], // Y coordinate (latitude)
      lng: point[0]  // X coordinate (longitude)
    }));

    // Determine occupancy status fill colour (occupied/vacant/other)
    const fillColor = this.getParkingSpaceFillColor(device);

    const polygon = new google.maps.Polygon({
      paths: paths,
      strokeColor: '#FFFFFF',
      strokeOpacity: 0.9,      // Thin, visible white outline
      strokeWeight: 1,
      fillColor: fillColor,
      fillOpacity: 1.0,
      clickable: true,
      zIndex: 10 // Ensure parking spaces render above lot polygons
    });

    polygon.setMap(this.map);
    this.spacePolygons.push(polygon);

    // Store the polygon in the map for quick access during occupancy updates
    if (device.GuidId) {
      this.spacePolygonMap.set(device.GuidId, polygon);
    }

    polygon.addListener('click', (event: any) => {
      this.ngZone.run(() => {
        // When drawing a selection polygon, treat clicks on space polygons as map clicks
        if (this.isPolygonDrawing) {
          this.addPolygonVertex(event);
        } else {
          this.selectDevice(device);
        }
      });
    });

    // Add label in the center of the parking space
    this.addParkingSpaceLabel(device, paths);

    // Add status indicator inside the parking space
    this.addParkingSpaceStatusIndicator(device, paths);
  }

  private getParkingSpaceFillColor(device: MapDevice): string {
    const isOccupied = this.isParkingSpaceOccupied(device);
    const status = (device.OccupancyStatus || '').toLowerCase();

    // Occupied spaces
    if (isOccupied) {
      return '#234923';
    }

    // Vacant spaces (explicitly marked as available/vacant)
    if (status === 'available' || status === 'vacant') {
      return '#aacf37';
    }

    // All other states (reserved, unknown, etc.) - grey
    return '#808080';
  }

  private addParkingSpaceLabel(device: MapDevice, paths: {lat: number, lng: number}[]) {
    // Calculate the center point of the polygon
    let totalLat = 0;
    let totalLng = 0;
    
    paths.forEach(point => {
      totalLat += point.lat;
      totalLng += point.lng;
    });
    
    const centerLat = totalLat / paths.length;
    const centerLng = totalLng / paths.length;

    // Slightly offset the label so it doesn't sit directly on top of the status dot
    const labelLat = centerLat + 0.00001; // ~1m north of center

    // Create label content
    const labelDiv = document.createElement('div');
    labelDiv.style.cssText = `
      background: rgba(255, 255, 255, 0.9);
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 2px 4px;
      font-size: 9px;
      font-weight: bold;
      color: #333;
      text-align: center;
      white-space: nowrap;
      pointer-events: none;
      box-shadow: 0 1px 2px rgba(0,0,0,0.2);
    `;
    labelDiv.textContent = device.Name;

    // Create the label marker
    const labelMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.showSpaceNames ? this.map : null,
      position: { lat: labelLat, lng: centerLng },
      content: labelDiv,
      zIndex: 1000 // Ensure labels appear above polygons
    });

    // Track the label for cleanup
    this.spaceLabels.push(labelMarker);
  }

  private addParkingSpaceStatusIndicator(device: MapDevice, paths: { lat: number; lng: number }[]) {
    if (!this.map) {
      return;
    }

    // Calculate the center point of the polygon
    let totalLat = 0;
    let totalLng = 0;

    paths.forEach(point => {
      totalLat += point.lat;
      totalLng += point.lng;
    });

    const indicatorLat = totalLat / paths.length;
    const indicatorLng = totalLng / paths.length;

    // Create the status dot element
    const statusDiv = document.createElement('div');
    statusDiv.style.cssText = `
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 2px solid #FFFFFF;
      background-color: ${this.getDeviceStatusColor(device.Status)};
      box-shadow: 0 1px 2px rgba(0,0,0,0.4);
    `;

    const statusMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.showStatusDots ? this.map : null,
      position: { lat: indicatorLat, lng: indicatorLng },
      content: statusDiv,
      zIndex: 1100
    });

    this.spaceStatusMarkers.push(statusMarker);

    if (device.GuidId) {
      this.spaceStatusMarkerMap.set(device.GuidId, statusMarker);
    }
  }

  onToggleStatusDots() {
    this.spaceStatusMarkers.forEach(marker => {
      marker.map = this.showStatusDots ? this.map : null;
    });
  }

  onToggleLotNames() {
    this.updateLotLabelVisibility();
  }

  toggleDeviceInfoCollapsed() {
    this.deviceInfoCollapsed = !this.deviceInfoCollapsed;
  }

  toggleConfigCollapsed() {
    this.configCollapsed = !this.configCollapsed;
  }

  toggleSpaceDetailsCollapsed() {
    this.spaceDetailsCollapsed = !this.spaceDetailsCollapsed;
  }

  toggleGatewayConfigCollapsed() {
    this.gatewayConfigCollapsed = !this.gatewayConfigCollapsed;
  }

  toggleSignConfigCollapsed() {
    this.signConfigCollapsed = !this.signConfigCollapsed;
  }

  toggleCarCounterConfigCollapsed() {
    this.carCounterConfigCollapsed = !this.carCounterConfigCollapsed;
  }

  toggleGenericConfigCollapsed() {
    this.genericConfigCollapsed = !this.genericConfigCollapsed;
  }

  onToggleSpaceNames() {
    this.spaceLabels.forEach(label => {
      label.map = this.showSpaceNames ? this.map : null;
    });
  }

  private drawDeviceMarker(device: MapDevice) {
    const coords = this.getDeviceCoordinates(device);
    if (!coords) return;

    const style = (this.deviceStyles as any)[device.DeviceType] || { color: '#808080', shape: 'circle' };
    const statusColor = this.getDeviceStatusColor(device.Status);
    const deviceTypeColor = style.color;

    // Create content element for the marker
    const markerElement = document.createElement('div');
    markerElement.style.position = 'relative';
    // Base size for most device markers
    markerElement.style.width = '16px';
    markerElement.style.height = '16px';
    markerElement.style.cursor = 'pointer';
    markerElement.title = `${device.Name} (${device.DeviceType})`;

    // Apply shape styling based on device type
    this.applyDeviceShape(markerElement, style?.shape || 'circle', deviceTypeColor, statusColor);

    // Make gateways visually larger using scale (do this AFTER shape styling)
    if (device.DeviceType === 'Gateway' || device.DeviceType === 'GuidanceLight') {
      const existingTransform = markerElement.style.transform || '';
      const scalePart = 'scale(1.4)';
      markerElement.style.transform = existingTransform
        ? `${existingTransform} ${scalePart}`
        : scalePart;
      markerElement.style.transformOrigin = 'center center';
    }

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: coords,
      map: this.map,
      content: markerElement,
      title: `${device.Name} (${device.DeviceType})`
    });

    marker.addListener('click', () => {
      this.ngZone.run(() => this.selectDevice(device));
    });

    this.markers.push(marker);
  }

  private applyDeviceShape(element: HTMLElement, shape: string, deviceTypeColor: string, statusColor: string) {
    // Clear any default styling
    element.style.backgroundColor = 'transparent';
    element.style.border = 'none';
    element.style.borderRadius = '0';
    element.style.boxShadow = 'none';
    element.style.width = '16px';
    element.style.height = '16px';

    switch (shape) {
      case 'triangle':
        this.createSVGShape(element, 'triangle', deviceTypeColor, statusColor);
        break;
      case 'triangleUp':
        this.createSVGShape(element, 'triangleUp', deviceTypeColor, statusColor);
        break;
        
      case 'square':
        element.style.backgroundColor = statusColor;
        element.style.border = 'none';
        element.style.borderRadius = '2px';
        break;
        
      case 'diamond':
        element.style.backgroundColor = statusColor;
        element.style.border = 'none';
        element.style.borderRadius = '2px';
        element.style.transform = 'rotate(45deg)';
        break;
        
      case 'hexagon':
        this.createSVGShape(element, 'hexagon', deviceTypeColor, statusColor);
        break;
        
      case 'star':
        this.createSVGShape(element, 'star', deviceTypeColor, statusColor);
        break;
        
      case 'circle':
      default:
        element.style.backgroundColor = statusColor;
        element.style.border = 'none';
        element.style.borderRadius = '50%';
        break;
    }
  }

  private createSVGShape(element: HTMLElement, shape: string, strokeColor: string, fillColor: string) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    svg.setAttribute('viewBox', '0 0 16 16');
    
    let shapeElement: SVGElement;
    
    switch (shape) {
      case 'triangle':
        shapeElement = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        // Triangle pointing DOWN (used for gateways)
        shapeElement.setAttribute('points', '2,3 14,3 8,15');
        break;
      case 'triangleUp':
        shapeElement = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        // Triangle pointing UP (used for guidance lights)
        shapeElement.setAttribute('points', '2,13 14,13 8,1');
        break;
        
      case 'hexagon':
        shapeElement = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        shapeElement.setAttribute('points', '4,2 12,2 15,8 12,14 4,14 1,8'); // Hexagon
        break;
        
      case 'star':
        shapeElement = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        shapeElement.setAttribute('points', '7.5,0 9.2,5.3 14.7,5.3 10.2,8.6 11.8,13.7 7.5,10.5 3.2,13.7 4.8,8.6 0.3,5.3 5.8,5.3'); // 5-pointed star
        break;
        
      default:
        shapeElement = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        shapeElement.setAttribute('cx', '8');
        shapeElement.setAttribute('cy', '8');
        shapeElement.setAttribute('r', '6.5');
        break;
    }
    
    // Apply styling
    shapeElement.setAttribute('fill', fillColor);
    shapeElement.setAttribute('stroke', 'none');
    
    svg.appendChild(shapeElement);
    element.appendChild(svg);
  }

  private getDeviceStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'online':
        return '#007bff';
      case 'warning':
        return '#f89c1c';
      case 'offline':
        return '#ec1e24';
      default:
        // Unknown / unassigned / any other state
        return '#d0d2d3';
    }
  }

  private isParkingSpaceOccupied(device: MapDevice): boolean {
    // Only use OccupiedSpaces integer as the single source of truth for parking spaces
    if (device.DeviceType === 'ParkingSpace') {
      return (device.OccupiedSpaces ?? 0) > 0;
    }
    
    return false; // Default to not occupied for non-parking spaces
  }

  private selectDevice(device: MapDevice) {
    this.selectedDevice = device;
    this.showDeviceInfo = true;
    
    // Clear previous device detail data but set loading states
    this.deviceDetail = null;
    this.isLoadingDeviceDetail = true;
    this.isLoadingMessages = true;
    
    // Show visual indicator on selected device
    this.showSelectedDeviceIndicator(device);
    
    // Load device details and messages in parallel for better UX
    this.loadDeviceDetail(device);
    this.loadDeviceMessages(device);
    // Note: drawDeviceConnections will be called after loadDeviceDetail completes
  }

  private createPreviewDeviceDetail(device: MapDevice): DeviceDetailResponse {
    return {
      Device: { ...device },
      DeviceSpecificData: device.AdditionalData ? this.normalizeKeys(device.AdditionalData) : null,
      RecentMessages: []
    };
  }

  private normalizeDeviceDetailResponse(data: DeviceDetailResponse, fallbackDevice: MapDevice): DeviceDetailResponse {
    const normalizedDevice = data?.Device ? { ...fallbackDevice, ...this.normalizeKeys(data.Device) } : { ...fallbackDevice };
    const normalizedSpecific = data?.DeviceSpecificData ? this.normalizeKeys(data.DeviceSpecificData) : null;
    const normalizedMessages = data?.RecentMessages ? data.RecentMessages.map(message => this.normalizeKeys(message)) : [];

    return {
      Device: normalizedDevice,
      DeviceSpecificData: normalizedSpecific,
      RecentMessages: normalizedMessages
    };
  }

  private normalizeKeys(value: any): any {
    if (Array.isArray(value)) {
      return value.map(item => this.normalizeKeys(item));
    }

    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc: any, key: string) => {
        const camelKey = key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
        acc[camelKey] = this.normalizeKeys((value as any)[key]);
        return acc;
      }, {} as any);
    }

    return value;
  }
  private loadDeviceDetail(device: MapDevice) {
    const request = {
      deviceType: device.DeviceType,
      configurationId: device.Id,
      chipId: device.ChipId
    };

    this.apiService.Post<DeviceDetailResponse>('api/InfrastructureMap/devicedetail', request)
      .then((data) => {
        if (this.selectedDevice && this.selectedDevice.Id === device.Id) {
          const normalized = this.normalizeDeviceDetailResponse(data, this.selectedDevice);
          const existingMessages = this.deviceDetail?.RecentMessages ?? [];

          this.deviceDetail = {
            ...normalized,
            RecentMessages: existingMessages
          };

          // Check if the server data differs from our current map data and sync if needed
          this.syncDeviceDataFromServer(device, normalized.Device);

          // Now that we have the detailed device data, draw the connections
          this.drawDeviceConnections(device);

          this.isLoadingDeviceDetail = false;
        }
      })
      .catch((error) => {
          console.error('Error loading device detail:', error);
          this.toastr.error('Failed to load device details');
        this.isLoadingDeviceDetail = false;
      });
  }
  private loadDeviceMessages(device: MapDevice) {
    // Only load messages for devices that have a chipId (hardware devices)
    if (!device.ChipId) {
      this.isLoadingMessages = false;
      return;
    }

    const request = {
      deviceType: device.DeviceType,
      configurationId: device.Id,
      chipId: device.ChipId
    };

    this.apiService.Post<RecentMessagesResponse>('api/InfrastructureMap/devicemessages', request)
      .then((data) => {
        if (this.selectedDevice?.Id === device.Id) {
          const normalizedMessages = data?.RecentMessages ? data.RecentMessages.map(message => this.normalizeKeys(message)) : [];
          const detail = this.deviceDetail ?? this.createPreviewDeviceDetail(this.selectedDevice);

          this.deviceDetail = {
            ...detail,
            RecentMessages: normalizedMessages
          };

          this.isLoadingMessages = false;
        }
      })
      .catch((error) => {
        console.error('Error loading device messages:', error);
        this.isLoadingMessages = false;
        // Don't show error toast for messages as it's not critical for UX
      });
  }

  private syncDeviceDataFromServer(mapDevice: MapDevice, serverDevice: any) {
    // Only sync parking spaces as they have occupancy data that can get out of sync
    if (mapDevice.DeviceType !== 'ParkingSpace') {
      return;
    }

    // Check if occupancy data differs between map and server
    const mapOccupied = this.isParkingSpaceOccupied(mapDevice);
    const serverOccupied = this.isParkingSpaceOccupiedFromServerData(serverDevice);

    if (mapOccupied !== serverOccupied) {
      console.log(`Syncing occupancy for space ${mapDevice.Name}: map shows ${mapOccupied ? 'occupied' : 'available'}, server shows ${serverOccupied ? 'occupied' : 'available'}`);
      
      // Update the map device with server data
      mapDevice.OccupiedSpaces = serverDevice.OccupiedSpaces ?? 0;
      mapDevice.OccupancyStatus = serverOccupied ? 'Occupied' : 'Available';
      
      // Update the visual representation
      this.updateParkingSpaceVisual(mapDevice);
      
      // Update the selected device data so properties pane shows correct info
      if (this.selectedDevice && this.selectedDevice.Id === mapDevice.Id) {
        this.selectedDevice.OccupiedSpaces = mapDevice.OccupiedSpaces;
        this.selectedDevice.OccupancyStatus = mapDevice.OccupancyStatus;
      }
    }
  }

  private isParkingSpaceOccupiedFromServerData(serverDevice: any): boolean {
    // Only use OccupiedSpaces integer as the single source of truth
    return (serverDevice.OccupiedSpaces ?? 0) > 0;
  }

  // Standardized Device Connection Drawing System
  private drawDeviceConnections(device: MapDevice) {
    // Clear existing connection lines
    this.clearConnectionLines();

    // Get device coordinates - can't draw connections without location
    const deviceCoords = this.getDeviceCoordinates(device);
    if (!deviceCoords) {
      return;
    }

    // Draw connections based on device type
    switch (device.DeviceType) {
      case 'ParkingSpace':
        this.drawParkingSpaceConnections(device, deviceCoords);
        break;
      case 'Gateway':
        this.drawGatewayConnections(device, deviceCoords);
        break;
      case 'GuidanceLight':
        this.drawGuidanceLightConnections(device, deviceCoords);
        break;
      case 'Sign':
        this.drawSignConnections(device, deviceCoords);
        break;
      // Add more device types as needed
      default:
        // Default behavior - just draw connection to gateway if it has one
        this.drawDefaultGatewayConnection(device, deviceCoords);
        break;
    }
  }

  private drawParkingSpaceConnections(device: MapDevice, deviceCoords: { lat: number, lng: number }) {
    const connections: Array<{ target: MapDevice, color: string, style: 'solid' | 'dashed', label: string }> = [];

    // Check for guidance light connection via port assignment in DeviceSpecificData
    const deviceSpecificData = this.deviceDetail?.DeviceSpecificData;
    const portAssignment = deviceSpecificData?.portAssignment || deviceSpecificData?.PortAssignment;
    
    if (portAssignment) {
      // Check for guidance light configuration ID (both cases)
      const guidanceLightConfigId = portAssignment.guidanceLightConfigurationId || 
                                   portAssignment.GuidanceLightConfigurationId;
      
      if (guidanceLightConfigId) {
        const guidanceLight = this.devices.find(d => 
          d.DeviceType === 'GuidanceLight' && d.Id === guidanceLightConfigId
        );
        
        if (guidanceLight) {
          const portNumber = portAssignment.portNumber || portAssignment.PortNumber;
          connections.push({
            target: guidanceLight,
            color: '#FF0000',
            style: 'dashed',
            label: `Guidance Light (Port ${portNumber || '?'})`
          });
        }
      }
    }

    // Connection to gateway (if it has one)
    if (device.GatewayChipId) {
      const gateway = this.devices.find(d => 
        d.DeviceType === 'Gateway' && d.ChipId === device.GatewayChipId
      );
      
      if (gateway) {
        connections.push({
          target: gateway,
          color: '#0066FF',
          style: 'dashed',
          label: 'Gateway'
        });
      }
    }

    // Draw all connections
    connections.forEach(connection => {
      const targetCoords = this.getDeviceCoordinates(connection.target);
      if (targetCoords) {
        this.drawConnectionLine(
          deviceCoords, 
          targetCoords, 
          connection.color, 
          connection.style,
          connection.label
        );
      }
    });
  }

  private drawGatewayConnections(device: MapDevice, deviceCoords: { lat: number, lng: number }) {
      // Show connections from gateway to all devices using it
    const connectedDevices = this.devices.filter(d => d.GatewayChipId === device.ChipId);
    
      connectedDevices.forEach(connectedDevice => {
      const connectedCoords = this.getDeviceCoordinates(connectedDevice);
      if (connectedCoords) {
        this.drawConnectionLine(
          deviceCoords, 
          connectedCoords, 
          '#0066FF', 
          'solid',
          `To ${connectedDevice.DeviceType}: ${connectedDevice.Name}`
          );
        }
      });
  }

  private drawGuidanceLightConnections(device: MapDevice, deviceCoords: { lat: number, lng: number }) {
    // Connection to gateway
    if (device.GatewayChipId) {
      const gateway = this.devices.find(d => 
        d.DeviceType === 'Gateway' && d.ChipId === device.GatewayChipId
      );
      
      if (gateway) {
        const gatewayCoords = this.getDeviceCoordinates(gateway);
        if (gatewayCoords) {
          this.drawConnectionLine(
            deviceCoords, 
            gatewayCoords, 
            '#0066FF', 
            'dashed',
            'Gateway'
        );
      }
    }
  }

    // Show parking spaces assigned to this guidance light via port assignments
    let portAssignments: any[] = [];
    
    if (device.AdditionalData?.PortAssignments && Array.isArray(device.AdditionalData.PortAssignments)) {
      portAssignments = device.AdditionalData.PortAssignments;
    } else if (this.deviceDetail?.DeviceSpecificData?.PortAssignments && Array.isArray(this.deviceDetail.DeviceSpecificData.PortAssignments)) {
      portAssignments = this.deviceDetail.DeviceSpecificData.PortAssignments;
    } else if (this.deviceDetail?.DeviceSpecificData?.portAssignments && Array.isArray(this.deviceDetail.DeviceSpecificData.portAssignments)) {
      portAssignments = this.deviceDetail.DeviceSpecificData.portAssignments;
    }
    
    if (portAssignments.length > 0) {
      portAssignments.forEach((portAssignment: any) => {
        const parkingSpaceId = portAssignment.ParkingSpaceId || portAssignment.parkingSpaceId;
        const portNumber = portAssignment.PortNumber || portAssignment.portNumber;
        const parkingSpaceName = portAssignment.ParkingSpaceName || portAssignment.parkingSpaceName;
        
        if (parkingSpaceId) {
          // Find the parking space by its GUID ID
          const assignedSpace = this.devices.find(d => 
            d.DeviceType === 'ParkingSpace' && d.GuidId === parkingSpaceId
          );

          if (assignedSpace) {
            const spaceCoords = this.getDeviceCoordinates(assignedSpace);
            if (spaceCoords) {
              this.drawConnectionLine(
                deviceCoords, 
                spaceCoords, 
                '#FF0000',
                'dashed',
                `Port ${portNumber}: ${parkingSpaceName || assignedSpace.Name}`
              );
            }
          }
        }
      });
    }
  }

  private drawSignConnections(device: MapDevice, deviceCoords: { lat: number, lng: number }) {
    // Connection to gateway
    this.drawDefaultGatewayConnection(device, deviceCoords);
    
    // Highlight target spaces for this sign
    this.highlightSignTargetSpaces(device);
  }

  private drawDefaultGatewayConnection(device: MapDevice, deviceCoords: { lat: number, lng: number }) {
    // Default behavior - draw connection to gateway if it has one
    if (device.GatewayChipId) {
      const gateway = this.devices.find(d => 
        d.DeviceType === 'Gateway' && d.ChipId === device.GatewayChipId
      );
      
      if (gateway) {
        const gatewayCoords = this.getDeviceCoordinates(gateway);
        if (gatewayCoords) {
          this.drawConnectionLine(
            deviceCoords, 
            gatewayCoords, 
            '#0066FF', 
            'dashed',
            'Gateway'
          );
        }
      }
    }
  }

  private drawConnectionLine(
    from: { lat: number, lng: number }, 
    to: { lat: number, lng: number }, 
    color: string, 
    style: 'solid' | 'dashed',
    label: string
  ) {
    if (!this.map) {
      return;
    }
    
    const polylineOptions: any = {
      path: [from, to],
      geodesic: true,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      zIndex: 100
    };

    // Add dashed line styling if needed
    if (style === 'dashed') {
      polylineOptions.icons = [{
        icon: {
          path: 'M 0,-1 0,1',
          strokeOpacity: 1,
          scale: 3,
          strokeColor: color
        },
        offset: '0',
        repeat: '15px'
      }];
      polylineOptions.strokeOpacity = 0.3;
    }

    const polyline = new google.maps.Polyline(polylineOptions);
    polyline.setMap(this.map);
    
    // Store connection line for cleanup
    this.connectionLines.push(polyline);

    // Add click listener to show connection info
    polyline.addListener('click', () => {
      console.log(`Connection: ${label}`);
    });
  }

  private clearConnectionLines() {
    this.connectionLines.forEach(line => line.setMap(null));
    this.connectionLines = [];
  }

  // Sign Target Space Highlighting
  private highlightedSpaces: string[] = [];

  private highlightSignTargetSpaces(device: MapDevice) {
    // Clear existing highlights
    this.clearHighlightedSpaces();

    const targetSpaceIds = this.deviceDetail?.DeviceSpecificData?.targetParkingSpaceIds;
    if (!targetSpaceIds || !Array.isArray(targetSpaceIds)) {
      return;
    }

    console.log(`Highlighting ${targetSpaceIds.length} target spaces for sign ${device.Name}`);

    // Always use purple fill color to highlight target spaces
    this.highlightSignTargetFill(targetSpaceIds);
  }

  private highlightSignTargetFill(targetSpaceIds: string[]) {
    targetSpaceIds.forEach(spaceId => {
      const polygon = this.spacePolygonMap.get(spaceId);
      if (polygon) {
        // Store original fill color and opacity
        if (!polygon.originalFillColor) {
          polygon.originalFillColor = polygon.get('fillColor');
          polygon.originalFillOpacity = polygon.get('fillOpacity');
        }

        // Apply distinctive fill color (purple/magenta - not part of status colors)
        polygon.setOptions({
          fillColor: '#9B59B6', // Purple fill to distinguish from status colors
          fillOpacity: 0.8 // Strong opacity to make it clearly visible
        });

        this.highlightedSpaces.push(spaceId);
      }
    });
  }

  private clearHighlightedSpaces() {
    this.highlightedSpaces.forEach(spaceId => {
      const polygon = this.spacePolygonMap.get(spaceId);
      if (polygon && polygon.originalFillColor) {
        // Restore original fill styling
        polygon.setOptions({
          fillColor: polygon.originalFillColor,
          fillOpacity: polygon.originalFillOpacity || 0.6
        });
      }
    });
    this.highlightedSpaces = [];
  }


  selectParkingLot(lot: ParkingLotPolygon | null) {
    // Parking lot click currently only logs for debugging; kept for potential future enhancements
    console.log('Parking lot clicked:', lot?.Name);
  }

  private forceReloadMapData() {
    // Clear current devices from the map and reload for current level selection
    this.devices = [];
    this.drawDevices();
    this.loadMapData();
  }

  manualRefresh() {
    // Preserve current selection across the mapdata reload (drawDevices() can clear it during intermediate states).
    if (this.selectedDevice) {
      this.pendingSelectionRestore = { id: this.selectedDevice.Id, deviceType: this.selectedDevice.DeviceType };
    } else {
      this.pendingSelectionRestore = null;
    }

    // Re-fetch the main map snapshot (devices + statuses) without clearing UI state up-front.
    // The selection (if any) will be restored after the response arrives.
    this.loadMapData();
  }

  onParkingLotChange() {
    console.log('Parking lot changed to:', this.selectedParkingLotId ?? 'ALL');

    // Rebuild level list based on selected lot
    this.allLevels = this.buildGlobalLevels(this.parkingLots, this.selectedParkingLotId);

    // Redraw lot polygons so only the selected lot (or all lots) are outlined
    this.drawParkingLotPolygons();

    // If the currently selected level number doesn't exist for this lot, reset to ALL
    if (this.selectedParkingLotId && this.selectedGlobalLevelNumber !== null) {
      const hasLevel = this.allLevels.includes(this.selectedGlobalLevelNumber);
      if (!hasLevel) {
        this.selectedGlobalLevelNumber = null;
      }
    }

    // If a specific lot is selected, center the map on that lot
    if (this.selectedParkingLotId) {
      this.centerMapOnParkingLot(this.selectedParkingLotId);
    }

    this.forceReloadMapData();
  }

  private centerMapOnParkingLot(lotId: string) {
    if (!this.map || !this.parkingLots || this.parkingLots.length === 0) {
      return;
    }

    const lot = this.parkingLots.find(l => l.Id === lotId);
    if (!lot || !lot.GeoCenterPoint || lot.GeoCenterPoint.length < 2) {
      return;
    }

    const centerLng = lot.GeoCenterPoint[0];
    const centerLat = lot.GeoCenterPoint[1];

    if (typeof centerLat !== 'number' || typeof centerLng !== 'number') {
      return;
    }

    const targetCenter = new google.maps.LatLng(centerLat, centerLng);

    // Smoothly pan the map to the lot center
    this.animateMapPanTo(targetCenter);
  }

  private animateMapPanTo(targetCenter: any, durationMs: number = 700) {
    if (!this.map) {
      return;
    }

    // Cancel any in-progress animation
    if (this.panAnimationFrame !== null) {
      cancelAnimationFrame(this.panAnimationFrame);
      this.panAnimationFrame = null;
    }

    const startCenter = this.map.getCenter();
    if (!startCenter) {
      this.map.setCenter(targetCenter);
      return;
    }

    const startLat = startCenter.lat();
    const startLng = startCenter.lng();
    const endLat = targetCenter.lat();
    const endLng = targetCenter.lng();

    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / durationMs, 1);

      // Ease-in-out for a nicer slide
      const easeT = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

      const currentLat = startLat + (endLat - startLat) * easeT;
      const currentLng = startLng + (endLng - startLng) * easeT;

      this.map.setCenter(new google.maps.LatLng(currentLat, currentLng));

      if (t < 1) {
        this.panAnimationFrame = requestAnimationFrame(step);
      } else {
        this.panAnimationFrame = null;
      }
    };

    this.panAnimationFrame = requestAnimationFrame(step);
  }

  onGlobalLevelChange() {
    console.log('Global level changed to:', this.selectedGlobalLevelNumber ?? 'ALL');
    this.forceReloadMapData();
  }

  closeDeviceInfo() {
    this.showDeviceInfo = false;
    this.selectedDevice = null;
    this.deviceDetail = null;
    
    // Clear device connections and selected indicator
    this.clearConnectionLines();
    this.clearSelectedDeviceIndicator();
  }

  // Method removed - no longer needed with per-lot level management

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString();
  }

  get parkingSpaceDetail(): any {
    if (this.selectedDevice?.DeviceType !== 'ParkingSpace') {
      return null;
    }

    return this.deviceDetail?.DeviceSpecificData ?? null;
  }

  get guidanceLightDetail(): any {
    if (this.selectedDevice?.DeviceType !== 'GuidanceLight') {
      return null;
    }

    return this.deviceDetail?.DeviceSpecificData ?? null;
  }

  get signDetail(): any {
    if (this.selectedDevice?.DeviceType !== 'Sign') {
      return null;
    }

    return this.deviceDetail?.DeviceSpecificData ?? null;
  }

  get gatewayDetail(): any {
    if (this.selectedDevice?.DeviceType !== 'Gateway') {
      return null;
    }

    return this.deviceDetail?.DeviceSpecificData ?? null;
  }

  get carCounterDetail(): any {
    if (this.selectedDevice?.DeviceType !== 'CarCounter') {
      return null;
    }

    return this.deviceDetail?.DeviceSpecificData ?? null;
  }

  getDeviceStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'online':
        return 'status-online';
      case 'warning':
        return 'status-warning';
      case 'offline':
        return 'status-offline';
      default:
        return 'status-unknown';
    }
  }

  private handleIndividualSpaceOccupancyEvent(event: any) {
    if (!event || !event.SpaceId) {
      return;
    }

    console.log('Individual space occupancy event:', event);

    // Find the parking space device by its GUID ID (Service Bus events use GUID)
    const device = this.devices.find(d => 
      d.DeviceType === 'ParkingSpace' && d.GuidId === event.SpaceId
    );

    if (!device) {
      console.log(`Space with GUID ${event.SpaceId} not found in current map view`);
      return;
    }

    const wasOccupied = this.isParkingSpaceOccupied(device);
    
    // Update occupancy based on the message type
    switch (event.MessageType) {
      case 'CarOnEvent':
        device.OccupiedSpaces = 1;
        device.OccupancyStatus = 'Occupied';
        console.log(`Space ${device.Name} (ID: ${device.Id}, GUID: ${device.GuidId}) is now OCCUPIED`);
        break;
      case 'CarOffEvent':
        device.OccupiedSpaces = 0;
        device.OccupancyStatus = 'Available';
        console.log(`Space ${device.Name} (ID: ${device.Id}, GUID: ${device.GuidId}) is now AVAILABLE`);
        break;
      default:
        console.log(`Unknown message type: ${event.MessageType}`);
        return;
    }

    const isNowOccupied = this.isParkingSpaceOccupied(device);
    
    // Only update the visual if occupancy status actually changed
    if (wasOccupied !== isNowOccupied) {
      console.log(`Space ${device.Name} occupancy changed: ${wasOccupied ? 'Occupied' : 'Available'} -> ${isNowOccupied ? 'Occupied' : 'Available'}`);
      this.updateParkingSpaceVisual(device);
      this.updatePropertiesPaneOccupancy(device);
    }
  }

  private updateParkingSpaceVisual(device: MapDevice) {
    // Find the specific polygon for this parking space and update only its stroke color
    if (!device.GuidId) {
      console.warn(`Cannot update visual for parking space ${device.Name} - no GUID ID`);
      return;
    }

    const polygon = this.spacePolygonMap.get(device.GuidId);
    if (!polygon) {
      console.warn(`Polygon not found for parking space ${device.Name} (GUID: ${device.GuidId})`);
      return;
    }

    // Calculate the new colors based on occupancy
    const newFillColor = this.getParkingSpaceFillColor(device);
    const isOccupied = this.isParkingSpaceOccupied(device);

    // Update visual styling for the polygon (preserve thin white outline)
    polygon.setOptions({
      strokeColor: '#FFFFFF',
      strokeOpacity: 0.9,
      strokeWeight: 1,
      fillColor: newFillColor
    });

    // Also update the status indicator colour based on device status
    const statusMarker = this.spaceStatusMarkerMap.get(device.GuidId);
    if (statusMarker && statusMarker.content) {
      const contentEl = statusMarker.content as HTMLElement;
      contentEl.style.backgroundColor = this.getDeviceStatusColor(device.Status);
    }

    console.log(`Updated polygon colors for space ${device.Name} to fill=${newFillColor} (${isOccupied ? 'occupied' : 'not occupied'})`);
  }

  // Search functionality
  onSearchQueryChange() {
    this.searchQuery = this.searchQuery.trim();

    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }

    if (this.searchQuery.length === 0) {
      this.clearSearch();
      return;
    }

    if (this.searchQuery.length < 2) {
      // Don't search for very short queries
      this.searchResults = [];
      this.showSearchResults = false;
      this.clearSearchMarkers();
      return;
    }

    // Debounce search to avoid heavy work on every keypress
    this.searchDebounceTimeout = setTimeout(() => {
      this.performSearch();
    }, 300);
  }
  
  private performSearch() {
    const query = this.searchQuery.toLowerCase();
    
    // Search devices by name and chip ID
    const matches = this.devices.filter(device => {
      const nameMatch = device.Name?.toLowerCase().includes(query);
      const chipIdMatch = device.ChipId?.toString().includes(query);
      return nameMatch || chipIdMatch;
    });

    // Limit number of results to keep UI responsive
    const MAX_RESULTS = 100;
    this.searchResults = matches.slice(0, MAX_RESULTS);
    
    this.showSearchResults = this.searchResults.length > 0;
    this.updateSearchMarkers();
  }
  
  private updateSearchMarkers() {
    // Clear existing search markers
    this.clearSearchMarkers();
    
    // Add markers for search results
    this.searchResults.forEach(device => {
      const coords = this.getDeviceCoordinates(device);
      if (coords) {
        this.createSearchMarker(device, coords);
      }
    });
  }
  
  private createSearchMarker(device: MapDevice, coords: {lat: number, lng: number}) {
    // Create a prominent search result marker
    const markerDiv = document.createElement('div');
    markerDiv.style.width = '30px';
    markerDiv.style.height = '30px';
    markerDiv.style.backgroundColor = '#FF6B35';
    markerDiv.style.border = '3px solid #FFFFFF';
    markerDiv.style.borderRadius = '50%';
    markerDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.4)';
    markerDiv.style.cursor = 'pointer';
    markerDiv.style.display = 'flex';
    markerDiv.style.alignItems = 'center';
    markerDiv.style.justifyContent = 'center';
    markerDiv.style.fontSize = '16px';
    markerDiv.style.color = 'white';
    markerDiv.style.fontWeight = 'bold';
    markerDiv.innerHTML = '🔍';
    
    const searchMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: coords,
      content: markerDiv,
      title: `Search Result: ${device.Name}`,
      zIndex: 1000 // High z-index to appear above other markers
    });
    
    // Add click handler
    markerDiv.addEventListener('click', () => {
      this.ngZone.run(() => {
        this.selectDevice(device);
        
        // Pan to the device location and zoom in
        const coords = this.getDeviceCoordinates(device);
        if (coords && this.map) {
          this.map.panTo(coords);
          this.map.setZoom(Math.max(this.map.getZoom(), 19)); // Zoom in if not already close
        }
        
        this.clearSearch(); // Close search after selecting
      });
    });
    
    this.searchMarkers.push(searchMarker);
  }
  
  private clearSearchMarkers() {
    this.searchMarkers.forEach(marker => {
      marker.map = null;
    });
    this.searchMarkers = [];
  }

  private showSelectedDeviceIndicator(device: MapDevice) {
    // Clear any existing selected device marker
    this.clearSelectedDeviceIndicator();
    
    // Get device coordinates
    const coords = this.getDeviceCoordinates(device);
    if (!coords) return;
    
    // Offset the indicator up and to the right by about one icon width/height
    const offsetCoords = {
      lat: coords.lat + 0.00001, // Small offset up (north) - about 1 meter
      lng: coords.lng + 0.00001  // Small offset right (east) - about 1 meter
    };
    
    // Create the info icon element
    const iconElement = document.createElement('div');
    iconElement.innerHTML = '<i class="fas fa-info-circle"></i>';
    iconElement.style.cssText = `
      width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #2196F3;
      color: white;
      border-radius: 50%;
      font-size: 10px;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      border: 2px solid white;
      cursor: pointer;
      z-index: 1000;
    `;
    
    // Create the marker
    this.selectedDeviceMarker = new google.maps.marker.AdvancedMarkerElement({
      position: offsetCoords,
      map: this.map,
      content: iconElement,
      title: `Selected: ${device.Name}`
    });
  }

  private clearSelectedDeviceIndicator() {
    if (this.selectedDeviceMarker) {
      this.selectedDeviceMarker.map = null;
      this.selectedDeviceMarker = null;
    }
  }
  
  clearSearch() {
    this.searchQuery = '';
    this.searchResults = [];
    this.showSearchResults = false;
    this.clearSearchMarkers();
  }
  

  private updatePropertiesPaneOccupancy(device: MapDevice) {
    // If this space is currently selected in the properties pane, update the display
    if (this.selectedDevice && this.selectedDevice.Id === device.Id) {
      this.selectedDevice.OccupiedSpaces = device.OccupiedSpaces;
      this.selectedDevice.OccupancyStatus = device.OccupancyStatus;
      
      // Trigger change detection to update the properties pane
      // The template will automatically reflect the updated values
    }
  }

  private enforceFlatOverheadView() {
  if (!this.map) {
    return;
  }
  
  try {
    if (this.map.setTilt) {
      this.map.setTilt(0);
    }
    if (this.map.setHeading) {
      this.map.setHeading(0);
    }
  } catch (e) {
    console.warn('Unable to enforce zero tilt/heading on map', e);
  }
}
}

















